/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import Table from "@mui/material/Table";
import DataTable from "../../examples/Tables/DataTable";
import data from "../dashboard/components/Projects/data";
import useApi from "../../components/Utils/api";
import MDInput from "../../components/MDInput";
import toast from "react-hot-toast";
import subDays from "date-fns/subDays";
import startOfMonth from "date-fns/startOfMonth";
import addMonths from "date-fns/addMonths";
import endOfMonth from "date-fns/endOfMonth";
import { useNavigate } from "react-router-dom";
import Icon from "@mui/material/Icon";
import BulkAssignMerchant from "./bulk-assign";
import PermissionControl from "../../components/Utils/PermissionControl";
import {useTranslation} from "react-i18next";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";

function Merchants() {
    const {t} = useTranslation();
  const { merchantTablecolumns } = data();
  const [merchants, setMerchants] = useState([]);
  const [filteredMerchants, setFilteredMerchants] = useState([]);
  const [unAssignedMerchants, setUnAssignedMerchants] = useState([]);
  const [merchantGroups, setMerchantGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const api = useApi();

  // Filter state
  const [filterSearch, setFilterSearch] = useState("");
  const [filterOrgName, setFilterOrgName] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const getMerchants = () => {
    const data = {
      "ownerName": "",
      "mid": "",
      "referenceId": ""
    };
    api.post("merchant/browse",data)
      .then((response) => {
        if(response.data.statusCodeValue === 200) {
          setMerchants(response.data.body);
        } else {
          toast.error(response.data.error);
        }
      })
      .catch((error) => {
          if (error.response && error.response.status === 403) {
              toast.error(t('you_do_not_have_permission_to_perform_this_action'), { id: "PermissionError" });
              return;
          }
        // No response (API down / network error) leaves error.response undefined
        console.log(error.response?.data ?? error);
        toast.error(t('something_went_wrong'), { id: "MerchantsLoadError" });
      })
  }
    const getMerchantGroups = () => {
        api.get("admin/browseMerchantGroup")
            .then((response) => {
                if(response.data.statusCodeValue === 200) {
                    setMerchantGroups(response.data.body);
                } else {
                    toast.error(response.data.error);
                }
            })
            .catch((error) => {
                if (error.response && error.response.status === 403) {
                    toast.error(t('you_do_not_have_permission_to_perform_this_action'), { id: "PermissionError" });
                    return;
                }
                // No response (API down / network error) leaves error.response undefined
        console.log(error.response?.data ?? error);
        toast.error(t('something_went_wrong'), { id: "MerchantsLoadError" });
            })
    }
    const getUnAssignedMerchants = () => {
        const data = {
            "ownerName": "",
            "mid": "",
            "referenceId": "RC000000000"
        };
        api.post("merchant/browse",data)
            .then((response) => {
                if(response.data.statusCodeValue === 200) {
                    setUnAssignedMerchants(response.data.body);
                } else {
                    toast.error(response.data.error);
                }
            })
            .catch((error) => {
                if (error.response && error.response.status === 403) {
                    toast.error(t('you_do_not_have_permission_to_perform_this_action'), { id: "PermissionError" });
                    return;
                }
                // No response (API down / network error) leaves error.response undefined
        console.log(error.response?.data ?? error);
        toast.error(t('something_went_wrong'), { id: "MerchantsLoadError" });
            })
    }

  useEffect(() => {
    getMerchants();
    getUnAssignedMerchants();
    getMerchantGroups();
  },[]);

  // Apply filters whenever merchants data or filter values change
  useEffect(() => {
    applyFilters();
  }, [merchants, filterSearch, filterOrgName, filterStatus, filterFromDate, filterToDate]);

  const applyFilters = () => {
    let result = [...merchants];
    const search = filterSearch.trim().toLowerCase();
    if (search) {
      result = result.filter(m =>
        m.mid?.toLowerCase().includes(search) ||
        m.companyName?.toLowerCase().includes(search) ||
        m.ownerName?.toLowerCase().includes(search)
      );
    }
    if (filterOrgName) {
      result = result.filter(m => m.orgName === filterOrgName);
    }
    if (filterStatus !== "") {
      result = result.filter(m => String(m.status) === filterStatus);
    }
    if (filterFromDate) {
      result = result.filter(m => m.createdDt && new Date(m.createdDt) >= new Date(filterFromDate));
    }
    if (filterToDate) {
      const toDateEnd = new Date(filterToDate);
      toDateEnd.setHours(23, 59, 59, 999);
      result = result.filter(m => m.createdDt && new Date(m.createdDt) <= toDateEnd);
    }
    setFilteredMerchants(result);
  };

  const resetFilters = () => {
    setFilterSearch("");
    setFilterOrgName("");
    setFilterStatus("");
    setFilterFromDate("");
    setFilterToDate("");
  };

  const rowHandler = (row, pageRow)  => {
    navigate(`/merchants/${pageRow.original.mid}`, {
      state: pageRow.original
    })
  }

  const columns = merchantTablecolumns;

  return (
    <DashboardLayout>
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Grid container justifyContent="space-between" alignItems="center">
                <MDBox>
                    <MDTypography variant="h5" fontWeight="medium">
                        {t('merchants')}
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="regular" color="secondary">
                        {filteredMerchants.length} {t('merchants')}, {merchantGroups.length} {t('organizations')}
                    </MDTypography>
                </MDBox>
                <MDBox display="flex">
                    <PermissionControl permission={{ restrict: true, module: "Merchants", subModule: "AssignToOrganization" }}>
                        <MDButton variant="contained" color="white" size="small" onClick={() => setOpen(true)} style={{marginRight: "16px"}}>
                            {t('assign_to_merchant_group')}
                        </MDButton>
                    </PermissionControl>
                    <PermissionControl permission={{ restrict: true, module: "Merchants", subModule: "Create" }}>
                      <MDButton variant="gradient" onClick={() => navigate('/merchants/new')} color="info">
                          {t('add_merchant')}
                      </MDButton>
                    </PermissionControl>
                </MDBox>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={12}>
            <MDBox sx={(theme) => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: "8px", background: theme.palette.white.main, overflow: "hidden" })}>
            {/* Filter Panel */}
            <MDBox px={2} py={1.5} sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    label={t('search')}
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <TextField
                    select
                    label={t('merchantTablecolumns.organization')}
                    value={filterOrgName}
                    onChange={(e) => setFilterOrgName(e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": { height: "38px" },
                      "& .MuiSelect-select": { fontSize: "0.8rem" },
                    }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>{t('all')}</MenuItem>
                    {merchantGroups.map((org) => (
                      <MenuItem key={org.referenceId} value={org.referenceCompanyName} style={{ fontSize: "0.8rem" }}>
                        {org.referenceCompanyName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <TextField
                    select
                    label={t('merchantTablecolumns.status')}
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": { height: "38px" },
                      "& .MuiSelect-select": { fontSize: "0.8rem" },
                    }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>{t('all')}</MenuItem>
                    <MenuItem value="1" style={{ fontSize: "0.8rem" }}>{t('active')}</MenuItem>
                    <MenuItem value="0" style={{ fontSize: "0.8rem" }}>{t('inactive')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <TextField
                    label={t('from_date')}
                    type="date"
                    value={filterFromDate}
                    onChange={(e) => setFilterFromDate(e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ shrink: true, style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <TextField
                    label={t('to_date')}
                    type="date"
                    value={filterToDate}
                    onChange={(e) => setFilterToDate(e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ shrink: true, style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg="auto">
                  <MDButton variant="outlined" color="secondary" onClick={resetFilters} size="small" style={{ fontSize: "0.75rem", padding: "5px 16px", whiteSpace: "nowrap" }}>
                    {t('reset')}
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>

            <DataTable
              table={{ columns: columns, rows: filteredMerchants }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              entriesPerPagePosition="bottom"
              canSearch={false}
              onRowClicked={rowHandler}
              disableRowDialog
            />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
        <BulkAssignMerchant setOpen={setOpen} open={open} unAssignedMerchants={unAssignedMerchants} merchantGroups={merchantGroups} getUnAssignedMerchants={getUnAssignedMerchants} />
      <Footer />
    </DashboardLayout>
  );
}

export default Merchants;
